import { createContext, useContext, useCallback, type ReactNode } from 'react';

/**
 * PWA Context - Service Worker Only (v1.0)
 * 
 * Strategy: De-emphasize PWA install prompts per Issue #606
 * - No install prompt UI or triggers
 * - Service worker continues for caching/performance
 * - Focus on mobile web experience
 * 
 * This context kept for backward compatibility with existing code
 * that calls showInstallPrompt() - now a no-op.
 */
interface PWAContextValue {
  showInstallPrompt: () => void; // No-op in v1.0
  hideInstallPrompt: () => void; // No-op in v1.0
  isPromptVisible: boolean; // Always false in v1.0
}

const PWAContext = createContext<PWAContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function usePWA() {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
}

interface PWAProviderProps {
  children: ReactNode;
}

export function PWAProvider({ children }: PWAProviderProps) {
  // No-op functions for v1.0 - kept for backward compatibility
  const showInstallPrompt = useCallback(() => {
    // Intentionally empty - no install prompts in v1.0
    // Future: May add TWA/native shell hooks here
  }, []);

  const hideInstallPrompt = useCallback(() => {
    // Intentionally empty
  }, []);

  const value = {
    showInstallPrompt,
    hideInstallPrompt,
    isPromptVisible: false, // Always false in v1.0
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
}